import * as React from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { Lightbox, LightboxType } from "../../../../../../../react-core/Html/Components/Lightbox";
import { Div } from "../../../../../../../react-core/Html/Div";
import { FormComponent } from "../../../../../../../react-core/Html/FormComponent";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import { UkMobileNumber } from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import { CustomerPhoneNumberType } from "../../../../../../../Wrap/Constants/Enums/Domain/CustomerPhoneNumberType";
import { EmailAddressType } from "../../../../../../../Wrap/Constants/Enums/EmailAddressType";
import { TaskRequestStatus } from "../../../../../../../Wrap/Constants/Enums/Tasks/TaskRequestStatus";
import { FullClientDetailModel } from "../../../../../../../Wrap/Models/Models/Customer/FullClientDetailModel";
import { HierarchyIdHelper } from "../../../../../../Shared/Common/HierarchyIdHelper";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { DownloadButtonAction } from "../../../../../../Shared/DownloadButton/Actions/DownloadButtonAction";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { Table } from "../../../../../../Shared/Table/Table";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { ValidatorContents } from "../../../../../Common/ValidatorHelper";
import { InstructionDocumentTypeRequestTriggerActionsCreator } from "../../../Common/Documents/Actions/InstructionDocumentTypeRequestTriggerActionsCreator";
import { processTypeEnum } from "../../../Common/Resource/Contents";
import { CustomerContextStoreStatic } from "../../Stores/Variable";
import { CustomerSearchAction } from "../../../ClientRecord/Action/CustomerSearchAction";

interface IESignaturePopupViewProps extends React.Props<ESignaturePopupView> {
    showBox: boolean;
    callBack: (isSubmitSuccess: boolean) => void;
    fullClientDetail: FullClientDetailModel;
    taskRequestList: Array<ISignatureTaskRequest>;
    processType: processTypeEnum;
    isJuniorSipp?: Boolean;
}

interface IESignaturePopupViewState {
    isLoading?: boolean;
    checkedRequestIds: number[];
    mobilePhone: string;
    taskRequestStatus: TaskRequestStatus;
    hasSubmitted: boolean;
    EmailAddress: string;
    GivenName: string;
    FamilyName: string;
}

interface ISignatureTaskRequest {
    fileName: string;
    taskRequestId: number;
}

export class ESignaturePopupView extends FormComponent<IESignaturePopupViewProps, IESignaturePopupViewState> {
    constructor(props: IESignaturePopupViewProps) {
        super(props);

        this.state = {
            isLoading: false,
            checkedRequestIds: [],
            mobilePhone: this.getPrimaryPhoneNumber(),
            hasSubmitted: false,
            taskRequestStatus: null,
            EmailAddress: null,
            GivenName: null,
            FamilyName: null
        };
    }

    public componentDidMount() {
        if (!this.props.isJuniorSipp) {
            let clientDetail = this.props.fullClientDetail;
            if (!clientDetail) {
                clientDetail = CustomerContextStoreStatic.getData();
            }
            const email = clientDetail?.EmailAddressDetails?.filter((x) => x?.EmailAddressType == EmailAddressType.Primary)?.[0]?.EmailAddress;
            const phoneNumber = clientDetail?.PhoneNumberDetails?.filter(
                (x) => x?.PhoneNumberType == CustomerPhoneNumberType.Mobile
            )?.[0]?.PhoneNumber?.Number;
            this.setState({
                EmailAddress: email,
                GivenName: clientDetail?.PersonalDetails?.GivenName,
                FamilyName: clientDetail?.PersonalDetails?.FamilyName,
                mobilePhone: phoneNumber
            });
        }
        else {
            CustomerSearchAction.getCustomerData(this.props.fullClientDetail.GuardianId, (result) => {
                this.setState({
                    EmailAddress: result?.EmailAddressDetails?.[0]?.EmailAddress,
                    GivenName: result?.PersonalDetails?.GivenName,
                    FamilyName: result?.PersonalDetails?.FamilyName,
                    mobilePhone: result?.PhoneNumberDetails?.filter(
                        (x) => x?.PhoneNumberType == CustomerPhoneNumberType.Mobile
                    )?.[0]?.PhoneNumber?.Number
                })
            });
        }
    }

    public render(): JSX.Element {
        return <Div>{this.props.showBox ? this.renderModal() : null}</Div>;
    }

    private renderModal(): JSX.Element {
        const { isLoading, taskRequestStatus, hasSubmitted } = this.state;
        let elementRender: JSX.Element;

        if (!hasSubmitted) {
            elementRender = <div>{this.getModalBody()}</div>;
        } else if (!isLoading) {
            if (taskRequestStatus == TaskRequestStatus.Complete) {
                elementRender = (
                    <p className="alert-success small">
                        You have successfully submitted the documents to your client for e-signature.
                        <br />
                        You can now close this window and continue to submit this application.
                    </p>
                );
            } else {
                elementRender = (
                    <div>
                        <Alert
                            alertType={AlertType.Error}
                            message="The document(s) failed to submit for e-signature.Please close this window and click the 'Select document(s)' button to try again."
                        />
                    </div>
                );
            }
        } else {
            elementRender = this.renderLoading();
        }

        return (
            <Lightbox
                show={this.props.showBox}
                title={"E-signature"}
                contentAfterTitle={null}
                onClose={() => {
                    this.props.callBack(hasSubmitted && taskRequestStatus == TaskRequestStatus.Complete);
                    this.setState({ hasSubmitted: false, checkedRequestIds: [] });
                }}
                type={LightboxType.Modal}
                unclosable={false}
                noOverlay={false}
                noAutofocus={false}
                cssWrapper="is-default-lightbox"
            >
                {elementRender}
            </Lightbox>
        );
    }

    private getModalBody(): JSX.Element {

        const config = [
            {
                title: "First name",
                data: this.state.GivenName,
            },
            {
                title: "Surname",
                data: this.state.FamilyName,
            },
            {
                title: "Email address",
                data: this.state.EmailAddress,
            },
            {
                title: "Mobile phone",
                data: (this.state.hasSubmitted && this.state.mobilePhone) ? (
                    this.state.mobilePhone
                ) : (
                <TextBoxEditor
                        required="Please enter mobile number"
                        value={this.state.mobilePhone}
                        onChange={(v) => this.setState({ mobilePhone: v })}
                        regex={{
                            errorMessage: ValidatorContents.InvalidMobilePhone,
                            value: UkMobileNumber,
                        }}
                    />
                ),
            },
        ];

        return (
            <>
                <h3 className="mb-11">{this.props.isJuniorSipp ? "Guardian Contact Details" : "Client Contact Details"}</h3>
                <hr className="heading-underlined-keyline"></hr>
                <div className="content">
                    <div className="row">
                        <div className="col-12 col-sm-auto col-middle">
                            <Typography type="common" config={config}></Typography>
                        </div>
                    </div>
                    <Accordion label="Incorrect details?">
                        <div className="col-12 col-sm-auto">
                            If any of the pre-populated data above is incorrect please return to step1 of the New
                            Business wizard and amend accordingly.
                        </div>
                        <div className="col-12 col-sm-auto">
                            You will then be able to return to the Documents & Declarations Page.
                        </div>
                    </Accordion>
                    <div className="row">
                        <Table
                            data={this.props.taskRequestList}
                            col={[
                                {
                                    title: "File name",
                                    key: "fileName",
                                    render: (data, row: ISignatureTaskRequest, index) => {
                                        return <a className="">{row.fileName}</a>;
                                    },
                                },

                                {
                                    title: "Select to be e-signed",
                                    key: "taskRequestId",
                                    render: (data, row: ISignatureTaskRequest, index) => {
                                        return (
                                            <CheckboxEditor
                                                cssWrapper={"square-checkbox mb-0"}
                                                onChange={(v) => {
                                                    const checked = this.state.checkedRequestIds;
                                                    if (v) {
                                                        this.setState({
                                                            checkedRequestIds: checked.concat(row.taskRequestId),
                                                        });
                                                    } else {
                                                        this.setState({
                                                            checkedRequestIds: checked.filter(
                                                                (ele) => ele != row.taskRequestId
                                                            ),
                                                        });
                                                    }
                                                }}
                                                value={false}
                                            />
                                        );
                                    },
                                },
                            ]}
                        />
                    </div>
                    {this.state.checkedRequestIds.length > 0 && (
                        <div className="row">
                            <p>
                                <button className="btn secondary" onClick={() => this.submit()}>
                                    Submit to client
                                </button>
                            </p>
                        </div>
                    )}
                </div>
            </>
        );
    }

    private submit() {
        let instructionId = 0;
        const parameters = getQueryParameters();
        const urlHierarchyId = parameters["id"];
        if (this.props.processType != processTypeEnum.NewBusiness) {
            instructionId = HierarchyIdHelper.generateHierarchyId(urlHierarchyId).entityId;
        } else {
            instructionId = urlHierarchyId;
        }

        if (this.isValid() == true) {
            this.setState({ isLoading: true, hasSubmitted: true });
            const { checkedRequestIds, mobilePhone } = this.state;
            const { fullClientDetail } = this.props;
            InstructionDocumentTypeRequestTriggerActionsCreator.submitEsignatureDocumentRequest(
                fullClientDetail.ActorId,
                instructionId,
                mobilePhone,
                checkedRequestIds,
                (result) => {
                    if (Number(result) > 0) {
                        DownloadButtonAction.getTaskRequestStatus(result, (callback) =>
                            this.onStatusChange(callback.Status, result)
                        );
                    }
                }
            );
        }
    }

    private onStatusChange(status: TaskRequestStatus, taskRequestId: string) {
        if (
            status == TaskRequestStatus.Pending ||
            status == TaskRequestStatus.Running ||
            status == TaskRequestStatus.Allocated ||
            status == TaskRequestStatus.PendingDelayed ||
            status == TaskRequestStatus.PendingHold
        ) {
            setTimeout(() => {
                DownloadButtonAction.getTaskRequestStatus(taskRequestId, (callback) =>
                    this.onStatusChange(callback.Status, taskRequestId)
                );
            }, 2000);
        } else if (status == TaskRequestStatus.Complete) {
            this.setState({ isLoading: false, taskRequestStatus: TaskRequestStatus.Complete });
        } else {
            //failed or cancelled
            this.setState({ isLoading: false, taskRequestStatus: status });
        }
    }

    private getPrimaryPhoneNumber() {
        let clientDetail = this.props.fullClientDetail;

        if (!clientDetail) {
            clientDetail = CustomerContextStoreStatic.getData();
        }

        return clientDetail?.PhoneNumberDetails?.filter(
            (x) => x?.PhoneNumberType == CustomerPhoneNumberType.Mobile
        )?.[0]?.PhoneNumber?.Number;
    }

    private renderLoading() {
        return <PartLoading isLoading={true}></PartLoading>;
    }
}
